import { debounce, DebouncedFunc } from "lodash-es";
import Stimulus from "../../helpers/stimulus";
import SubmitController from "./submit_controller";
import ApplicationController from "../application_controller";
import handleError from "../../helpers/handle_error";

type BeforeSubmitEventDetail = { promises: Array<Promise<any>> };

export default class FormController extends ApplicationController<HTMLFormElement> {
  static targets = ["validationsContainer"];
  static values = { isSubmitting: Boolean, debounce: Number };

  declare readonly validationsContainerTarget: HTMLElement;
  declare readonly hasValidationsContainerTarget: boolean;
  declare isSubmittingValue: boolean;
  declare debounceValue: number | undefined;

  submitFunction!:
    DebouncedFunc<(event: StimulusEvent & SubmitEvent) => void> | ((event: StimulusEvent & SubmitEvent) => void);

  connect() {
    if (this.debounceValue) {
      this.submitFunction = debounce(this._submit, this.debounceValue);
    } else {
      this.submitFunction = this._submit;
    }
  }

  submit(event: StimulusEvent & SubmitEvent) {
    this.submitFunction(event);
  }

  _submit(event: StimulusEvent & SubmitEvent) {
    event.preventDefault();
    this.isSubmittingValue = true;
    const beforeSubmitEvent = this.dispatch("beforeSubmit", { detail: { promises: [] } });
    const { promises } = beforeSubmitEvent.detail as BeforeSubmitEventDetail;
    Promise.all(promises)
      .then(() => {
        if (beforeSubmitEvent.defaultPrevented) {
          this.isSubmittingValue = false;
          return;
        }
        this.dispatch("submitting", { detail: { submitter: event.submitter } });
      })
      .catch((e) => {
        handleError(e);
        this.isSubmittingValue = false;
      });
  }

  showValidations(event: StimulusEvent) {
    const { errorAttributes } = event.detail.data;
    const errorMessagesHtml = event.detail.html;
    errorAttributes.split(" ").forEach((name: String) => {
      const nameRegex = new RegExp(`\\[${name}\\]`);
      const errorElements = [...this.validateableElements].filter((ele) => ele.name.match(nameRegex));
      errorElements.forEach((element) => {
        element.setAttribute("aria-invalid", "true");
      });
    });
    this.validationsContainerTarget.innerHTML = errorMessagesHtml;
    this.validationsContainerTarget.removeAttribute("hidden");
  }

  clearValidations() {
    this.validateableElements.forEach((element) => {
      element.removeAttribute("aria-invalid");
    });
    this.validationsContainerTarget.toggleAttribute("hidden", true);
  }

  resetSubmitting() {
    this.isSubmittingValue = false;
  }

  isSubmittingValueChanged() {
    // Value callbacks trigger before submitControllers are connected
    Promise.resolve().then(() => {
      this.submitControllers.forEach((controller) => {
        controller.isSubmittingValue = this.isSubmittingValue;
      });
    });
  }

  get validateableElements() {
    return this.element.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      "input[name], select[name], textarea[name]",
    );
  }

  get submitControllers(): Array<SubmitController> {
    return [
      ...this.element.querySelectorAll<HTMLButtonElement>('button[type="submit"]'),
      ...document.querySelectorAll<HTMLButtonElement>(`button[type="submit"][form="${this.element.id}"]`),
    ].map((ele) => Stimulus.getController(ele, SubmitController)!);
  }
}
